// src/context/ThemeContext.tsx
// App-wide light/dark theme. Defaults to the OS/browser preference and keeps
// following it live until the operator manually toggles, at which point the
// explicit choice is persisted and system changes are ignored.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'glowhire-theme';

function systemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function storedTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
}

type ThemeContextValue = {
  theme: Theme;
  /** True once the operator has manually overridden the system preference. */
  isOverridden: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  /** Clears the manual override and resumes following the OS preference. */
  resetToSystem: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => storedTheme() ?? systemTheme());
  const [isOverridden, setIsOverridden] = useState<boolean>(() => storedTheme() !== null);

  // Apply immediately and whenever it changes.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow live OS changes only while no manual override is in effect.
  useEffect(() => {
    if (isOverridden || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (e: MediaQueryListEvent) => {
      setThemeState(e.matches ? 'light' : 'dark');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [isOverridden]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    setIsOverridden(true);
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const resetToSystem = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setIsOverridden(false);
    setThemeState(systemTheme());
  }, []);

  const value = useMemo(
    () => ({ theme, isOverridden, setTheme, toggleTheme, resetToSystem }),
    [theme, isOverridden, setTheme, toggleTheme, resetToSystem],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
